import React, { useState, useEffect, useRef } from 'react';
import { HashRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import MultiStepForm from './components/Form/MultiStepForm';
import CustomerPortal from './components/Portal/CustomerPortal';
import CustomerAccount from './components/Portal/CustomerAccount';
import AdminPanel from './components/Admin/AdminPanel';
import AdminGate from './components/Auth/AdminGate';
import PortalGate from './components/Auth/PortalGate';
import SafeIcon from './common/SafeIcon';
import { supabase } from './lib/supabase';

const LOGO_URL = "https://smilevase.com/images/smilevase-logo.png";

// ─── Install Prompt Hook ───────────────────────────────────────────────────
function useInstallPrompt() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setIsInstalled(isStandalone);

    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    setIsIOS(ios);

    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => { setIsInstalled(true); setInstallPrompt(null); });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const triggerInstall = async () => {
    if (isIOS) { setShowIOSInstructions(true); return; }
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') { setIsInstalled(true); setInstallPrompt(null); }
  };

  const canInstall = !isInstalled && (installPrompt !== null || isIOS);
  return { canInstall, isInstalled, isIOS, triggerInstall, showIOSInstructions, setShowIOSInstructions };
}

// ─── iOS Instructions Modal ────────────────────────────────────────────────
function IOSInstallModal({ onClose }) {
  return (
    <div className="fixed inset-0 z-[200] flex items-end justify-center px-4 pb-4" onClick={onClose}>
      <div className="absolute inset-0 bg-charcoal/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-[2rem] p-8 w-full max-w-sm shadow-2xl space-y-5" onClick={e => e.stopPropagation()}>
        <div className="text-center space-y-2">
          <img src={LOGO_URL} alt="SmileVase" className="h-12 w-auto mx-auto" />
          <h3 className="text-xl font-serif text-charcoal">Add to Home Screen</h3>
          <p className="text-sm text-sage-500">Install SmileVase for a better experience.</p>
        </div>
        <ol className="space-y-4">
          <li className="flex items-start gap-3">
            <div className="w-7 h-7 bg-blush-100 rounded-full flex items-center justify-center text-blush-500 font-bold text-sm shrink-0 mt-0.5">1</div>
            <div className="text-sm text-charcoal">Tap the <strong>Share</strong> button at the bottom of your browser <span className="ml-1 inline-flex items-center justify-center w-6 h-6 bg-sage-100 rounded text-sage-500 text-xs">⬆</span></div>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-7 h-7 bg-blush-100 rounded-full flex items-center justify-center text-blush-500 font-bold text-sm shrink-0 mt-0.5">2</div>
            <p className="text-sm text-charcoal">Scroll down and tap <strong>"Add to Home Screen"</strong></p>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-7 h-7 bg-blush-100 rounded-full flex items-center justify-center text-blush-500 font-bold text-sm shrink-0 mt-0.5">3</div>
            <p className="text-sm text-charcoal">Tap <strong>"Add"</strong> to confirm</p>
          </li>
        </ol>
        <button onClick={onClose} className="btn-secondary w-full">Got it</button>
      </div>
    </div>
  );
}

// ─── Mobile Bottom Nav ─────────────────────────────────────────────────────
function MobileBottomNav({ session, onSignOut, installProps }) {
  const location = useLocation();
  const { canInstall, triggerInstall } = installProps;
  const isPortal = location.pathname === '/portal';
  const isHome = location.pathname === '/';
  const isAccount = location.pathname === '/account';

  return (
    <nav className="bottom-nav fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-sage-100 flex items-center justify-around px-2 pt-3 md:hidden shadow-[0_-4px_20px_-2px_rgba(0,0,0,0.06)]">

      {/* Order */}
      <Link
        to="/"
        className={`flex flex-col items-center gap-1 px-3 py-1 rounded-2xl transition-all ${isHome ? 'text-blush-400' : 'text-sage-400'}`}
      >
        <SafeIcon name="Flower2" className="text-2xl" />
        <span className="text-[10px] font-bold uppercase tracking-wider">Order</span>
      </Link>

      {/* My Orders */}
      <Link
        to="/portal"
        className={`flex flex-col items-center gap-1 px-3 py-1 rounded-2xl transition-all ${isPortal ? 'text-blush-400' : 'text-sage-400'}`}
      >
        <SafeIcon name="Package" className="text-2xl" />
        <span className="text-[10px] font-bold uppercase tracking-wider">My Orders</span>
      </Link>

      {/* My Account — routes to account page if logged in, portal (login) if not */}
      <Link
        to={session ? '/account' : '/portal'}
        className={`flex flex-col items-center gap-1 px-3 py-1 rounded-2xl transition-all ${isAccount ? 'text-blush-400' : 'text-sage-400'}`}
      >
        <SafeIcon name="User" className="text-2xl" />
        <span className="text-[10px] font-bold uppercase tracking-wider">Account</span>
      </Link>

      {/* Install — only shows when installable */}
      {canInstall && (
        <button
          onClick={triggerInstall}
          className="flex flex-col items-center gap-1 px-3 py-1 rounded-2xl text-sage-400 transition-all"
        >
          <SafeIcon name="Download" className="text-2xl" />
          <span className="text-[10px] font-bold uppercase tracking-wider">Install</span>
        </button>
      )}

      {/* Sign Out — only when logged in and install not showing */}
      {session && !canInstall && (
        <button
          onClick={onSignOut}
          className="flex flex-col items-center gap-1 px-3 py-1 rounded-2xl text-sage-400 transition-all active:text-red-400"
        >
          <SafeIcon name="LogOut" className="text-2xl" />
          <span className="text-[10px] font-bold uppercase tracking-wider">Sign Out</span>
        </button>
      )}
    </nav>
  );
}

// ─── Layout ────────────────────────────────────────────────────────────────
function Layout({ children, isEmbed }) {
  const location = useLocation();
  const [session, setSession] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const isAdminRoute = location.pathname === '/admin';
  const hideChrome = isEmbed || location.pathname === '/';
  const installProps = useInstallPrompt();

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
      if (session) checkAdminStatus(session);
    };

    const checkAdminStatus = async (session) => {
      const { data } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', session.user.id)
        .single();
      setIsAdmin(data?.is_admin || false);
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      if (sess) checkAdminStatus(sess);
      else setIsAdmin(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  // Admin route — clean desktop layout only
  if (isAdminRoute) {
    return (
      <div className="min-h-screen flex flex-col w-full">
        <header className="bg-white border-b border-sage-100 py-4 px-4 md:px-8 lg:px-12 flex justify-between items-center sticky top-0 z-50 w-full">
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="SmileVase" className="h-12 w-auto" />
          </Link>
          <nav className="flex items-center gap-4 text-sm font-medium text-sage-600">
            <Link to="/portal" className="hover:text-blush-400 flex items-center gap-2">
              <SafeIcon name="User" /> <span className="hidden sm:inline">Portal</span>
            </Link>
            <button onClick={handleSignOut} className="ml-2 bg-sage-50 hover:bg-sage-100 text-sage-500 p-2 rounded-full transition-colors" title="Sign Out">
              <SafeIcon name="LogOut" />
            </button>
          </nav>
        </header>
        <main className="flex-1 flex flex-col w-full">{children}</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col w-full">
      {/* Desktop header */}
      {!hideChrome && (
        <header className="hidden md:flex bg-white border-b border-sage-100 py-4 px-4 md:px-8 lg:px-12 justify-between items-center sticky top-0 z-50 w-full">
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="SmileVase" className="h-12 w-auto" />
          </Link>
          <nav className="flex items-center gap-4 md:gap-6 text-sm font-medium text-sage-600">
            <Link to="/portal" className="hover:text-blush-400 flex items-center gap-2">
              <SafeIcon name="Package" /> <span>My Orders</span>
            </Link>
            <Link to={session ? '/account' : '/portal'} className="hover:text-blush-400 flex items-center gap-2">
              <SafeIcon name="User" /> <span>My Account</span>
            </Link>
            {isAdmin && (
              <Link to="/admin" className="hover:text-blush-400 flex items-center gap-2">
                <SafeIcon name="Settings" /> <span>Admin</span>
              </Link>
            )}
            {installProps.canInstall && (
              <button onClick={installProps.triggerInstall} className="flex items-center gap-2 text-blush-400 font-semibold hover:text-blush-500 transition-colors">
                <SafeIcon name="Download" /> Add to Home Screen
              </button>
            )}
            <button onClick={handleSignOut} className="ml-2 bg-sage-50 hover:bg-sage-100 text-sage-500 p-2 rounded-full transition-colors" title="Sign Out">
              <SafeIcon name="LogOut" />
            </button>
          </nav>
        </header>
      )}

      {/* Mobile header */}
      {!hideChrome && (
        <header className="md:hidden bg-white border-b border-sage-100 py-3 px-4 flex justify-between items-center sticky top-0 z-50 w-full">
          <Link to="/">
            <img src={LOGO_URL} alt="SmileVase" className="h-10 w-auto" />
          </Link>
          {isAdmin && (
            <Link to="/admin" className="text-sage-400 p-2">
              <SafeIcon name="Settings" className="text-xl" />
            </Link>
          )}
        </header>
      )}

      <main className={`flex-1 flex flex-col w-full ${!hideChrome ? 'has-bottom-nav md:pb-0' : ''}`}>
        {children}
      </main>

      {/* Desktop footer */}
      {!hideChrome && (
        <footer className="hidden md:block bg-sage-50 border-t border-sage-100 py-8 text-center text-sm text-sage-500 mt-auto w-full">
          <img src={LOGO_URL} alt="SmileVase" className="h-8 w-auto mx-auto mb-4 opacity-50 grayscale" />
          <p>&copy;{new Date().getFullYear()} SmileVase. All rights reserved.</p>
        </footer>
      )}

      {/* Mobile bottom nav */}
      {!hideChrome && (
        <MobileBottomNav
          session={session}
          onSignOut={handleSignOut}
          installProps={installProps}
        />
      )}

      {installProps.showIOSInstructions && (
        <IOSInstallModal onClose={() => installProps.setShowIOSInstructions(false)} />
      )}
    </div>
  );
}

// ─── App ───────────────────────────────────────────────────────────────────
export default function App({ isEmbed = false, primaryColor }) {
  return (
    <HashRouter>
      <Layout isEmbed={isEmbed}>
        <Routes>
          <Route path="/" element={<MultiStepForm primaryColor={primaryColor} />} />
          <Route path="/portal" element={<PortalGate><CustomerPortal /></PortalGate>} />
          <Route path="/account" element={<PortalGate><CustomerAccount /></PortalGate>} />
          <Route path="/admin" element={<AdminGate><AdminPanel /></AdminGate>} />
        </Routes>
      </Layout>
    </HashRouter>
  );
}
